import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { router, usePathname, type Href } from 'expo-router';
import { Command } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ELEVATION,
  Z_INDEX,
  withWeight,
} from '../../theme';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useGlobalShortcuts } from '../../hooks/useGlobalShortcuts';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useStructuresStore } from '../../store/structuresStore';
import { useServiceAreasStore } from '../../store/serviceAreasStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { useEnsureScout } from '../../hooks/useEnsureScout';
import { useObservationGuidance } from '../../hooks/useObservationGuidance';
import { useOpenProperty } from '../../hooks/useOpenProperty';
import { useOpenMapPathsOverlay } from '../../hooks/useOpenMapPathsOverlay';
import { DialogPortal } from '../common/DialogPortal';
import { useEscapeToClose } from '../../utils/escapeStack';
import { CURSOR_POINTER } from '../../utils/webStyles';
import {
  groupCommandsForDisplay,
  flattenSections,
  nextHighlightIndex,
  type CommandItem,
} from './commands';

const PALETTE_WIDTH = 560;
const MIN_ADDRESS_QUERY_LENGTH = 3;
const ADDRESS_SEARCH_DEBOUNCE_MS = 250;
const MAX_ADDRESS_RESULTS = 8;
const LISTBOX_ID = 'command-palette-listbox';

function itemDomId(id: string): string {
  return `command-palette-item-${id}`;
}

/** Assembles the live command list. Not itself pure (reads stores/route) —
 *  the pure filter/group/highlight math lives in commands.ts. */
function useCommandItems(query: string, onClose: () => void): CommandItem[] {
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);
  const isAgent = Boolean(user?.agentProfile);
  const { requireAuth } = useAuthGate();
  const { ensureScoutThen } = useEnsureScout();
  const { guideThen } = useObservationGuidance();
  const openProperty = useOpenProperty();
  const openMapPathsOverlay = useOpenMapPathsOverlay();
  const toggleLayersPanel = useUIStore((s) => s.toggleLayersPanel);
  const toggleMapKey = useUIStore((s) => s.toggleMapKey);
  const presentationActive = useUIStore((s) => s.presentationActive);
  const requestSlideshowOpen = useUIStore((s) => s.requestSlideshowOpen);
  const requestSlideshowClose = useUIStore((s) => s.requestSlideshowClose);
  const requestPlanPanelOpen = useUIStore((s) => s.requestPlanPanelOpen);
  const requestMapFocus = useUIStore((s) => s.requestMapFocus);
  const ownPlans = useDefensePlansStore((s) => s.ownPlans);
  const setActiveOwnPlan = useDefensePlansStore((s) => s.setActiveOwnPlan);
  const startNewPlan = useDefensePlansStore((s) => s.startNewPlan);
  const searchResults = useStructuresStore((s) => s.searchResults);

  const navigateToMap = useCallback(() => {
    if (pathname !== '/') router.push('/(tabs)' as Href);
  }, [pathname]);

  // Every "Map" command (and address selection) navigates to the map first
  // when run from elsewhere — same one-shot-store-then-navigate shape the
  // rail's own actions use (they're plain uiStore flips a not-yet-mounted
  // map screen picks up on mount, same as pendingMapFocus/
  // slideshowOpenRequestId already do for cross-route callers).
  const runOnMap = useCallback(
    (fn: () => void) => {
      navigateToMap();
      fn();
      onClose();
    },
    [navigateToMap, onClose]
  );

  return useMemo(() => {
    const goTo: CommandItem[] = [
      {
        id: 'goto-map',
        label: 'Map',
        group: 'goTo',
        keywords: ['community', 'home'],
        run: () => {
          navigateToMap();
          onClose();
        },
      },
      {
        id: 'goto-property',
        label: 'Property',
        group: 'goTo',
        keywords: ['my property', 'claim'],
        run: () => {
          openProperty();
          onClose();
        },
      },
      {
        id: 'goto-news',
        label: 'News',
        group: 'goTo',
        keywords: ['feed'],
        run: () => {
          router.push('/(tabs)/feed' as Href);
          onClose();
        },
      },
      {
        id: 'goto-funding',
        label: 'Funding',
        group: 'goTo',
        run: () => {
          router.push('/(tabs)/funding' as Href);
          onClose();
        },
      },
      {
        id: 'goto-guidance',
        label: 'Guidance',
        group: 'goTo',
        keywords: ['how redi works', 'help', 'tour'],
        run: () => {
          openMapPathsOverlay();
          onClose();
        },
      },
      {
        id: 'goto-profile',
        label: 'Profile',
        group: 'goTo',
        keywords: ['account', 'settings'],
        run: () => {
          router.push('/profile' as Href);
          onClose();
        },
      },
    ];
    if (isAgent) {
      goTo.push(
        {
          id: 'goto-agent-home',
          label: 'Agent home',
          group: 'goTo',
          run: () => {
            router.push('/agent' as Href);
            onClose();
          },
        },
        {
          id: 'goto-agent-clients',
          label: 'Client properties',
          group: 'goTo',
          run: () => {
            router.push('/agent/clients' as Href);
            onClose();
          },
        },
        {
          id: 'goto-agent-checklists',
          label: 'Checklists',
          group: 'goTo',
          run: () => {
            router.push('/agent/checklists' as Href);
            onClose();
          },
        }
      );
    }

    const mapCommands: CommandItem[] = [
      {
        id: 'map-layers',
        label: 'Layers',
        group: 'map',
        shortcut: 'L',
        keywords: ['basemap', 'satellite', 'overlay', 'fire hazard'],
        run: () => runOnMap(toggleLayersPanel),
      },
      {
        id: 'map-key',
        label: 'Key',
        group: 'map',
        shortcut: 'K',
        keywords: ['legend'],
        run: () => runOnMap(toggleMapKey),
      },
      {
        id: 'map-present',
        label: presentationActive ? 'Stop presenting observations' : 'Present observations',
        group: 'map',
        shortcut: 'P',
        keywords: ['slideshow', 'presentation'],
        run: () => runOnMap(presentationActive ? requestSlideshowClose : requestSlideshowOpen),
      },
      {
        id: 'plan-open',
        label: 'Open last defense plan',
        group: 'map',
        keywords: ['draw a defense plan'],
        run: () =>
          runOnMap(() =>
            requireAuth(() => requestPlanPanelOpen(), {
              message: 'Sign in to draw a defense plan',
            })
          ),
      },
      {
        id: 'plan-new',
        label: 'New plan',
        group: 'map',
        keywords: ['defense plan', 'new defense plan'],
        run: () =>
          runOnMap(() =>
            requireAuth(
              () => {
                startNewPlan();
                requestPlanPanelOpen();
              },
              { message: 'Sign in to draw a defense plan' }
            )
          ),
      },
      {
        id: 'new-observation',
        label: 'New observation',
        group: 'map',
        shortcut: 'N',
        keywords: ['post', 'create', 'scout'],
        run: () => runOnMap(() => ensureScoutThen(() => guideThen('/post/create'))),
      },
    ];

    const plans: CommandItem[] = ownPlans.map((plan) => ({
      id: `plan-${plan.id}`,
      label: plan.title,
      group: 'plans',
      run: () =>
        runOnMap(() => {
          setActiveOwnPlan(plan.id);
          requestPlanPanelOpen();
        }),
    }));

    const addresses: CommandItem[] =
      query.trim().length >= MIN_ADDRESS_QUERY_LENGTH
        ? searchResults.slice(0, MAX_ADDRESS_RESULTS).map((structure) => ({
            id: `address-${structure.id}`,
            label: structure.address || 'Unknown address',
            group: 'addresses',
            keywords: [structure.city, structure.state, structure.zip].filter((v): v is string =>
              Boolean(v)
            ),
            run: () => {
              navigateToMap();
              requestMapFocus(structure.id);
              onClose();
            },
          }))
        : [];

    return [...goTo, ...mapCommands, ...plans, ...addresses];
  }, [
    query,
    isAgent,
    navigateToMap,
    runOnMap,
    onClose,
    openProperty,
    openMapPathsOverlay,
    toggleLayersPanel,
    toggleMapKey,
    presentationActive,
    requestSlideshowOpen,
    requestSlideshowClose,
    requireAuth,
    requestPlanPanelOpen,
    startNewPlan,
    ensureScoutThen,
    guideThen,
    ownPlans,
    setActiveOwnPlan,
    searchResults,
    requestMapFocus,
  ]);
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The ⌘K command palette dialog itself. Built directly on DialogPortal
 * (not AdaptiveSheet): AdaptiveSheet's dialog presentation hard-codes a
 * centered position (`alignItems/justifyContent: 'center'` in its overlay
 * style, not a prop), but this palette needs to sit near the top third of
 * the viewport the way every other launcher-style palette does — so this
 * reimplements just the entrance (`dataSet={{ rediAnim: 'dialog-scrim' |
 * 'dialog-card' }}`, the same CSS keyframes app/+html.tsx already defines)
 * and Z_INDEX.dialog, without borrowing AdaptiveSheet's centered layout.
 */
function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<TextInput>(null);

  const items = useCommandItems(query, onClose);
  const sections = useMemo(() => groupCommandsForDisplay(items, query), [items, query]);
  const flat = useMemo(() => flattenSections(sections), [sections]);

  const flatRef = useRef(flat);
  flatRef.current = flat;
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  // Unlike AddressSearchBar, the palette has no map viewport to derive a
  // single `currentAreaId` from — it can be opened from any screen, even
  // before the map ever mounted this session. So it sweeps every active
  // service area instead of one; ensureAreaLoadedForSearch no-ops above
  // SEARCH_PRELOAD_MAX_STRUCTURES per area, so this stays cheap.
  const activeAreaIdsKey = useServiceAreasStore((s) =>
    s.areas
      .filter((a) => a.active)
      .map((a) => a.id)
      .sort()
      .join(',')
  );
  // Search only scans shards already in `_structuresByShard`; re-run it as
  // the sweep above loads more in, same reasoning as AddressSearchBar's
  // loadedShardCount dependency.
  const loadedShardCount = useStructuresStore((s) => Object.keys(s._structuresByShard).length);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setHighlightedIndex(0);
    inputRef.current?.focus();
    return () => {
      // Drop any in-flight address search so the next open (or
      // AddressSearchBar, which reads the same store) doesn't inherit a
      // stale result list.
      useStructuresStore.getState().clearSearch();
    };
  }, [isOpen]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!isOpen || trimmed.length < MIN_ADDRESS_QUERY_LENGTH) {
      if (isOpen) useStructuresStore.getState().clearSearch();
      return;
    }
    const timer = setTimeout(() => {
      void useStructuresStore.getState().searchStructures(trimmed);
      // Load whatever's missing for every active area in the background —
      // see the comment on activeAreaIdsKey above. Each call is idempotent,
      // so re-running this on every loadedShardCount tick is a no-op once a
      // given area is fully swept.
      const activeAreaIds = activeAreaIdsKey ? activeAreaIdsKey.split(',') : [];
      for (const areaId of activeAreaIds) {
        void useStructuresStore.getState().ensureAreaLoadedForSearch(areaId);
      }
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, isOpen, activeAreaIdsKey, loadedShardCount]);

  useEscapeToClose(isOpen, onClose);

  // Up/Down/Enter, capture phase — same reasoning as escapeStack: RN Web's
  // TextInput stops propagation on every keydown at the bubble phase, so a
  // bubble-phase document listener would never see these while the input
  // (which is focused for the whole time the palette is open) has the
  // caret.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => nextHighlightIndex(i, flatRef.current.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => nextHighlightIndex(i, flatRef.current.length, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const item = flatRef.current[highlightedIndexRef.current];
        item?.run();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen]);

  if (!isOpen) return null;

  const activeItem = flat[highlightedIndex];

  return (
    <DialogPortal>
      <View
        style={[styles.overlay, { backgroundColor: colors.overlay }]}
        testID="command-palette-scrim"
        // @ts-expect-error dataSet is a react-native-web prop (drives the
        // scrim fade-in keyframe in app/+html.tsx's motion CSS); not in RN's
        // type defs — same pattern as AdaptiveSheet's DialogPresentation.
        dataSet={{ rediAnim: 'dialog-scrim' }}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessible={false}
          accessibilityLabel="Close command palette"
        />
        <View
          testID="command-palette"
          role="dialog"
          aria-modal
          aria-label="Command palette"
          style={[
            styles.card,
            ELEVATION.xl,
            {
              backgroundColor: colors.surfaceElevated,
              borderColor: colors.border,
              width: Math.min(PALETTE_WIDTH, windowWidth - SPACING.xl * 2),
            },
          ]}
          onStartShouldSetResponder={() => true}
          // @ts-expect-error dataSet is a react-native-web prop (drives the
          // card fade+scale keyframe in app/+html.tsx's motion CSS); not in
          // RN's type defs.
          dataSet={{ rediAnim: 'dialog-card' }}>
          <View style={[styles.inputRow, { borderBottomColor: colors.border }]}>
            <Command size={16} color={colors.textTertiary} />
            <TextInput
              ref={inputRef}
              testID="command-palette-input"
              value={query}
              onChangeText={setQuery}
              placeholder="Type a command or search…"
              placeholderTextColor={colors.textTertiary}
              autoFocus
              autoCorrect={false}
              autoCapitalize="none"
              style={[styles.input, { color: colors.text }]}
              accessibilityLabel="Command palette search"
              role="combobox"
              aria-expanded
              aria-controls={LISTBOX_ID}
              aria-activedescendant={activeItem ? itemDomId(activeItem.id) : undefined}
            />
          </View>
          <ScrollView
            style={styles.list}
            keyboardShouldPersistTaps="handled"
            nativeID={LISTBOX_ID}
            aria-label="Commands"
            // 'listbox' is valid ARIA but missing from RN's Role union —
            // spread it in untyped rather than widen role's type everywhere.
            {...({ role: 'listbox' } as object)}>
            {sections.length === 0 ? (
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                No matching commands
              </Text>
            ) : (
              sections.map((section) => (
                <View key={section.group}>
                  <Text style={[styles.groupLabel, { color: colors.textTertiary }]}>
                    {section.label}
                  </Text>
                  {section.items.map((item) => {
                    const index = flat.indexOf(item);
                    const highlighted = index === highlightedIndex;
                    return (
                      <Pressable
                        key={item.id}
                        testID={`command-palette-item-${item.id}`}
                        nativeID={itemDomId(item.id)}
                        role="option"
                        aria-selected={highlighted}
                        accessible={false}
                        // Pointer MOVE, not hover-in: rows that appear under
                        // a resting cursor (on open, or as the list filters)
                        // fire hover-in without the mouse moving, which
                        // stole the highlight from the first row.
                        onPointerMove={() => setHighlightedIndex(index)}
                        onPress={() => item.run()}
                        style={[
                          styles.row,
                          highlighted && { backgroundColor: colors.hoverSurface },
                          CURSOR_POINTER,
                        ]}>
                        <Text style={[styles.rowLabel, { color: colors.text }]} numberOfLines={1}>
                          {item.label}
                        </Text>
                        {item.shortcut && (
                          <Text style={[styles.rowShortcut, { color: colors.textTertiary }]}>
                            {item.shortcut}
                          </Text>
                        )}
                      </Pressable>
                    );
                  })}
                </View>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </DialogPortal>
  );
}

export const SHORTCUT_ROWS: { keys: string; label: string }[] = [
  { keys: '⌘K / Ctrl K', label: 'Open the command palette' },
  { keys: '/', label: 'Search address (map)' },
  { keys: 'L', label: 'Toggle map layers' },
  { keys: 'K', label: 'Toggle the map key' },
  { keys: 'P', label: 'Present observations' },
  { keys: 'N', label: 'New observation' },
  { keys: '?', label: 'Show this list' },
];

/** Shown under a "Plan" heading, and only while the plan editor is open. */
export const PLAN_SHORTCUT_ROWS: { keys: string; label: string }[] = [
  { keys: '1 to 8', label: 'Pick a defense' },
  { keys: 'Enter', label: 'Finish drawing' },
  { keys: 'Esc', label: 'Cancel' },
  { keys: '⌫', label: 'Undo a point' },
  { keys: 'Delete', label: 'Remove selected' },
  { keys: '⌘Z / Ctrl Z', label: 'Undo' },
  { keys: '↑ ↓', label: 'Move through defenses' },
  { keys: 'A', label: 'Assign' },
];

/** The rows the "?" dialog lists: the global ones, plus the Plan section
 *  while the editor is open. */
export function shortcutSections(
  planEditorOpen: boolean
): { title: string | null; rows: { keys: string; label: string }[] }[] {
  const sections: { title: string | null; rows: { keys: string; label: string }[] }[] = [
    { title: null, rows: SHORTCUT_ROWS },
  ];
  if (planEditorOpen) sections.push({ title: 'Plan', rows: PLAN_SHORTCUT_ROWS });
  return sections;
}

/** Small "?" help dialog listing every global shortcut. Same DialogPortal +
 *  entrance treatment as the palette, at a narrower fixed width since it's
 *  just a list. */
function ShortcutsHelpDialog({ isOpen, onClose }: CommandPaletteProps) {
  const { colors } = useTheme();
  const planEditorOpen = useUIStore((s) => s.planPanelOpen);
  useEscapeToClose(isOpen, onClose);
  if (!isOpen) return null;
  return (
    <DialogPortal>
      <View
        style={[styles.overlay, { backgroundColor: colors.overlay }]}
        testID="shortcuts-help-scrim"
        // @ts-expect-error dataSet is a react-native-web prop; see the same
        // comment on CommandPalette's scrim above.
        dataSet={{ rediAnim: 'dialog-scrim' }}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessible={false}
          accessibilityLabel="Close keyboard shortcuts"
        />
        <View
          testID="shortcuts-help-dialog"
          role="dialog"
          aria-modal
          aria-label="Keyboard shortcuts"
          style={[
            styles.card,
            styles.helpCard,
            ELEVATION.xl,
            { backgroundColor: colors.surfaceElevated, borderColor: colors.border },
          ]}
          onStartShouldSetResponder={() => true}
          // @ts-expect-error dataSet is a react-native-web prop; see the same
          // comment on CommandPalette's card above.
          dataSet={{ rediAnim: 'dialog-card' }}>
          <Text style={[styles.helpTitle, { color: colors.text }]}>Keyboard shortcuts</Text>
          {shortcutSections(planEditorOpen).map((section) => (
            <View key={section.title ?? 'global'}>
              {section.title && (
                <Text
                  style={[
                    styles.groupLabel,
                    styles.helpGroupLabel,
                    { color: colors.textTertiary },
                  ]}>
                  {section.title}
                </Text>
              )}
              {section.rows.map((row) => (
                <View key={row.keys} style={styles.helpRow}>
                  <Text style={[styles.rowLabel, { color: colors.text }]}>{row.label}</Text>
                  <Text style={[styles.rowShortcut, { color: colors.textTertiary }]}>
                    {row.keys}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>
    </DialogPortal>
  );
}

/**
 * Mounted once in app/_layout.tsx. Owns the palette's and the "?" help
 * dialog's open state, and is where useGlobalShortcuts is wired up — every
 * global keyboard shortcut lives behind this one mount point.
 */
export function CommandPaletteHost() {
  const isDesktopShell = useDesktopShell();
  const pathname = usePathname();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const toggleLayersPanel = useUIStore((s) => s.toggleLayersPanel);
  const toggleMapKey = useUIStore((s) => s.toggleMapKey);
  const presentationActive = useUIStore((s) => s.presentationActive);
  const requestSlideshowOpen = useUIStore((s) => s.requestSlideshowOpen);
  const requestSlideshowClose = useUIStore((s) => s.requestSlideshowClose);
  const requestMapSearchFocus = useUIStore((s) => s.requestMapSearchFocus);
  const { ensureScoutThen } = useEnsureScout();
  const { guideThen } = useObservationGuidance();

  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  useGlobalShortcuts({
    onTogglePalette: () => setPaletteOpen((v) => !v),
    onFocusSearch: () => requestMapSearchFocus(),
    onToggleLayers: () => toggleLayersPanel(),
    onToggleKey: () => toggleMapKey(),
    onTogglePresent: () => (presentationActive ? requestSlideshowClose() : requestSlideshowOpen()),
    onNewObservation: () => ensureScoutThen(() => guideThen('/post/create')),
    onOpenHelp: () => setHelpOpen(true),
    isOnMapRoute: pathname === '/',
  });

  if (!isDesktopShell) return null;

  return (
    <>
      <CommandPalette isOpen={paletteOpen} onClose={closePalette} />
      <ShortcutsHelpDialog isOpen={helpOpen} onClose={closeHelp} />
    </>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    zIndex: Z_INDEX.dialog,
  },
  card: {
    marginTop: '14%',
    borderRadius: BORDER_RADIUS.xl,
    borderWidth: 1,
    overflow: 'hidden',
    maxHeight: '70%',
  },
  helpCard: {
    width: 360,
    padding: SPACING.lg,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.md,
    height: 52,
    borderBottomWidth: 1,
  },
  input: {
    flex: 1,
    ...TYPOGRAPHY.body,
    height: '100%',
    outlineStyle: 'none' as any,
  },
  list: {
    maxHeight: 360,
  },
  groupLabel: {
    ...TYPOGRAPHY.overline,
    paddingTop: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.md,
    height: 40,
  },
  rowLabel: {
    ...TYPOGRAPHY.body,
    flex: 1,
  },
  rowShortcut: {
    ...withWeight(TYPOGRAPHY.caption, 'semibold'),
  },
  emptyText: {
    ...TYPOGRAPHY.body,
    padding: SPACING.lg,
    textAlign: 'center',
  },
  helpGroupLabel: {
    paddingHorizontal: 0,
    paddingTop: SPACING.md,
  },
  helpTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.sm,
  },
  helpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACING.xs,
  },
});
